import { MessageService, SelectItem } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CommonService } from 'src/app/services/common.service';

import { Component, OnInit } from '@angular/core';

import { EditMarkData } from 'src/app/models/edit-mark-data';
import { Mark } from 'src/app/models/mark';
import { ProgressService } from 'src/app/services/progress.service';
import { EditMarkService } from 'src/app/services/edit-mark.service';
import { Race } from 'src/app/models/race';
import { RaceClass } from 'src/app/models/race-class';
import { RaceProgressRegistration } from 'src/app/models/race_progress_registration';
import { SettingsService } from 'src/app/services/settings.service';
import { TimePeriodPipe } from 'src/app/pipes/time-period.pipe';

@Component({
  selector: 'app-edit-competitor',
  templateUrl: './edit-competitor.component.html',
  styleUrls: ['./edit-competitor.component.scss'],
})
export class EditCompetitorComponent implements OnInit {
  marks: EditMarkData[] = [];
  timeOffset!: string;
  raceDate!: Date;

  race!: Race;
  classRace!: RaceClass;
  competitor!: RaceProgressRegistration;
  dnfs: SelectItem[];
  penalty: number = 0;

  constructor(
    private progressService: ProgressService,
    public appSettings: SettingsService,
    private dynamicDialogRef: DynamicDialogRef,
    private dynamicDialogConfig: DynamicDialogConfig,
    private editMarkService: EditMarkService,
    private commonService: CommonService,
    private timePeriodPipe: TimePeriodPipe,
    public messageService: MessageService
  ) {
    this.dnfs = [
      { label: '1', value: 0 },
      { label: '2', value: 1 },
      { label: '3', value: 2 },
      { label: '4', value: 3 },
      { label: '5', value: 4 },
      { label: '6', value: 5 },
    ];
  }

  ngOnInit() {
    const { timeOffset, raceDate, race, classRace, competitor } = this.dynamicDialogConfig.data;
    this.race = race;
    this.classRace = classRace;
    this.competitor = competitor;
    this.timeOffset = timeOffset;
    this.raceDate = raceDate;
    this.penalty = this.competitor.penalty;
    this.loadMarksData();
  }

  closeDialog(refresh: boolean): void {
    this.dynamicDialogRef.close(refresh);
  }

  changeDnf(e: any) {
    if (!e.value) {
      this.competitor.DNFreason = '';
    }
  }

  saveDnfPenalty() {
    const penalty = this.penalty ? this.penalty : 0;
    const penaltyComment = this.competitor.penaltyComment;
    const dnf = this.competitor.DNF;
    const dnfReason = this.competitor.DNFreason;

    this.progressService.saveDnfPenalty(penalty, penaltyComment, dnf, dnfReason, this.race.rUid, this.classRace.clUid, this.competitor.registration.user.cUid).then(() => {
      this.messageService.clear();
      this.messageService.add({
        severity: 'success',
        summary: this.appSettings.translate[this.appSettings.selectedLang]['saved'],
      });
    });
  }

  loadMarksData() {
    this.progressService.getCompetitorMarks(this.race, this.classRace, this.competitor).subscribe({
      next: (marks) => {
        this.marks = marks;
        this.sortMarks();
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  sortMarks() {
    this.marks.sort((a, b) => {
      if (a.markTime < b.markTime) return -1;
      if (a.markTime > b.markTime) return 1;
      return 0;
    });

    const finish = this.marks.find((mark) => mark.type === 'finish' || mark.type === 'finish/mark');
    if (finish && finish.markTime == 0) {
      const idx = this.marks.indexOf(finish);
      this.marks.push(...this.marks.splice(idx, 1));
    }

    let prevMark;
    let prevNotDeletedMark;
    for (const mark of this.marks) {
      if (prevMark && mark.markTime != 0 && prevNotDeletedMark) {
        const prevTime = mark.delDate ? prevMark.markTime : prevNotDeletedMark.markTime;
        mark.timeSincePreviousMark = this.timePeriodPipe.transform(mark.markTime - prevTime, 'HH:mm:ss');
      }

      prevMark = mark;
      if (!mark.delDate) {
        prevNotDeletedMark = mark;
      }
    }
  }

  showEditMarkDialog(mark: EditMarkData) {
    const hasFinish = this.marks.some((mark) => mark.type === 'finish');
    const dialogRef = this.editMarkService
      .openMarkEditor(mark, this.timeOffset, 'edit', this.dynamicDialogConfig.header, hasFinish);
    
    dialogRef?.onClose.subscribe((newMarkData: EditMarkData) => {
      if (!newMarkData || (mark.markDate && newMarkData.markDate.getTime() == mark.markDate.getTime())) return;

      if (mark.type == 'start') this.changeStart(newMarkData);
      if (mark.type !== 'start') this.changeMark(mark, newMarkData);
    });
  }

  newMark() {
    const newMark = new EditMarkData({ markDate: this.raceDate, timeOffset: this.timeOffset }, 'mark');
    const hasFinish = this.marks.some((mark) => mark.type === 'finish');
    const dialogRef = this.editMarkService
      .openMarkEditor(newMark, this.timeOffset, 'new', this.dynamicDialogConfig.header, hasFinish);
    
    dialogRef?.onClose.subscribe((newMarkData: EditMarkData) => {
      console.log(newMarkData);
      if (!newMarkData) return;

      const newMark = new Mark({
        marshal: newMarkData.marshal,
        time: newMarkData.markDate,
      });

      this.progressService
        .saveMark(newMark.marshal, newMark.time ? newMark.time.getTime() : null, this.race.rUid, this.classRace.clUid, this.competitor.registration.user.cUid)
        .then(() => {
          this.loadMarksData();
        });
    });
  }

  undoMark(mark: EditMarkData) {
    this.commonService.confirm({
      message: this.appSettings.translate[this.appSettings.selectedLang]['restore_mark_question'],
      accept: () => {
        const newMark = new Mark({
          marshal: mark.marshal,
          time: mark.markDate,
        });

        this.progressService
          .saveMark(newMark.marshal, newMark.time ? newMark.time.getTime() : null, this.race.rUid, this.classRace.clUid, this.competitor.registration.user.cUid)
          .then(() => {
            this.loadMarksData();
          });
      },
    });
  }

  deleteMark(mark: EditMarkData) {
    this.commonService.confirm({
      message: this.appSettings.translate[this.appSettings.selectedLang]['delete_mark_question'],
      accept: () => {
        const oldMark = new Mark({
          marshal: mark.marshal,
          time: mark.markDate.getTime(),
        });

        this.progressService
          .deleteMark(oldMark, this.race, this.classRace, this.competitor)
          .then(() => {
            this.loadMarksData();
          });
      },
    });
  }

  changeStart(newMarkData: EditMarkData) {
    this.progressService
      .saveStart(newMarkData.markDate.getTime(), this.race.rUid, this.classRace.clUid, this.competitor.registration.user.cUid)
      .then(() => {
        this.loadMarksData();
      });
  }

  changeMark(mark: EditMarkData, newMarkData: EditMarkData) {
    const promises: Promise<Object | undefined | void>[] = [];
    const oldMark = new Mark({
      marshal: mark.marshal,
      time: mark.markTime,
    });
    const newMark = new Mark({
      marshal: newMarkData.marshal,
      time: newMarkData.markDate,
    });

    if (mark.type !== 'start') {
      promises.push(this.progressService.saveMark(newMark.marshal, newMark.time ? newMark.time.getTime() : null, this.race.rUid, this.classRace.clUid, this.competitor.registration.user.cUid));
    }

    Promise.all(promises)
      .then(() => {
        this.loadMarksData();
      });
  }
}
